import { CopyOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import type { TableColumnsType } from 'antd'
import { Button, Card, Form, Input, message, Modal, Select, Table, Tooltip, Typography } from 'antd'
import copy from 'copy-to-clipboard'
import React, { useEffect, useState } from 'react'

import { AppAdmin } from 'api/app-admin'
import Breadcrumb from 'component/breadcrumb/breadcrumb'
import ProtectedButton from 'component/button/protected-button'
import { BREADCRUMBS } from 'constants/breadcrumb'
import { FEATURE } from 'constants/feature'
import { DEFAULT_PAGE_OPTIONS, DEFAULT_PAGE_SIZE } from 'constants/pagination'
import { CONFIG_SETTING_QUERY_KEY } from 'hooks/setting/use-config-setting'
import { logError } from 'utils/logger'

import './style.scss'

interface DataType {
  key: any
  name: string
  value: string
  isActive: boolean
}

export default function Setting() {
  const queryClient = useQueryClient()
  const [form] = Form.useForm()
  const [filterForm] = Form.useForm()
  const [dataSetting, setDataSetting] = useState([])
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  })
  const [total, setTotal] = useState<number>(0)
  const [filter, setFilter] = useState<any>({})
  const [ellipsis] = useState(true)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalMode, setModalMode] = useState('create')

  useEffect(() => {
    getDataSetting({ ...pagination, ...filter })
  }, [pagination, filter])

  const getDataSetting = async (payload: any) => {
    try {
      const res = await AppAdmin.getListSetting(payload)
      if (res?.data.success === true) {
        setDataSetting(res?.data.data)
        setTotal(res?.data.totalCount)
      }
    } catch (error) {
      logError('Error: ', error)
    }
  }

  const showEditModal = async (record: any) => {
    setIsModalOpen(true)
    setModalMode('update')
    form.setFieldsValue(record)
  }

  const handleClickCreate = () => {
    form.resetFields() // Clear form fields
    setModalMode('create') // Set mode to create
    setIsModalOpen(true)
  }

  const handleCancel = () => {
    setIsModalOpen(false)
    form.resetFields()
  }

  const handleOk = () => {
    form
      .validateFields()
      .then(async (values) => {
        if (modalMode === 'update') {
          const { key: _, ...dataEdit } = values
          const response = await AppAdmin.settingUpdate(dataEdit)
          setIsModalOpen(false)
          message.open({
            type: 'success',
            content: response.data.message,
          })
          await getDataSetting({ ...pagination, ...filter })
          // Refetch config setting global (ipfs_gateway_base_url, contracts, ...)
          await queryClient.invalidateQueries({ queryKey: CONFIG_SETTING_QUERY_KEY })
        } else {
          const { key: _, ...createData } = values
          const response = await AppAdmin.settingUpdate(createData)
          setIsModalOpen(false)
          message.open({
            type: 'success',
            content: response.data.message,
          })
          await getDataSetting({ ...pagination, ...filter })
          // Refetch config setting global (ipfs_gateway_base_url, contracts, ...)
          await queryClient.invalidateQueries({ queryKey: CONFIG_SETTING_QUERY_KEY })
        }
      })
      .catch((error) => {
        message.open({
          type: 'error',
          content: error.message ? error.message : error.errorFields[0].errors[0],
        })
        // Error message format = "[errorField] [reason]"
        if (error && error.message) {
          form.setFields([
            {
              name: error.message.split(' ')[0],
              errors: [error.message],
            },
          ])
        }
      })
  }

  const columns: TableColumnsType<DataType> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Typography.Text
            style={ellipsis ? { width: 300 } : undefined}
            ellipsis={ellipsis ? { tooltip: text } : false}
          >
            {text}
          </Typography.Text>
          <Tooltip title="Copy">
            <Button
              type="link"
              icon={<CopyOutlined />}
              onClick={() => {
                copy(text)
                message.success('Copied Success!')
              }}
              style={{ marginLeft: '8px' }}
            />
          </Tooltip>
        </div>
      ),
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      render: (text) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Typography.Text
            style={ellipsis ? { width: 300 } : undefined}
            ellipsis={ellipsis ? { tooltip: text } : false}
          >
            {text}
          </Typography.Text>
          <Tooltip title="Copy">
            <Button
              type="link"
              icon={<CopyOutlined />}
              onClick={() => {
                copy(text)
                message.success('Copied Success!')
              }}
              style={{ marginLeft: '8px' }}
            />
          </Tooltip>
        </div>
      ),
    },
    {
      title: 'IsActive',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (status) =>
        status === true ? (
          <span className="status-success">Active</span>
        ) : (
          <span className="status-fail">UnActive</span>
        ),
    },
    {
      title: 'Action',
      key: 'action',
      render: (record) => (
        <>
          <ProtectedButton
            className="btn-edit"
            feature={FEATURE.SETTING}
            requireCRUD
            onClick={() => showEditModal(record)}
          >
            Edit
          </ProtectedButton>
        </>
      ),
    },
  ]

  const dataSettingList = dataSetting.map((item: DataType, i: number) => ({
    key: i.toString(),
    name: item.name,
    value: item.value,
    isActive: item.isActive,
  }))

  const statusOptions = [
    {
      value: false,
      label: 'UnActive',
    },
    {
      value: true,
      label: 'Active',
    },
  ]

  const additionalItems = [BREADCRUMBS.SETTING]

  return (
    <>
      <div className="main-section setting">
        <Breadcrumb additionalItems={additionalItems} />
        <div className="main-title" style={{ marginBottom: 20 }}>
          Setting
          <span className="new-button">
            <ProtectedButton
              type="primary"
              feature={FEATURE.SETTING}
              requireCRUD
              onClick={handleClickCreate}
            >
              Create New
            </ProtectedButton>
          </span>
        </div>

        <Card style={{ margin: '0 auto' }}>
          <div>
            <Form
              form={filterForm}
              layout="inline"
              onFinish={(values) => {
                setFilter(values)
                setPagination({ ...pagination, pageIndex: 0 })
              }}
              style={{ marginBottom: 20, marginTop: 10 }}
            >
              <Form.Item label="Name" name="name">
                <Input allowClear placeholder="Enter Name" />
              </Form.Item>
              <Form.Item>
                <Button type="primary" htmlType="submit">
                  Search
                </Button>
              </Form.Item>
            </Form>
          </div>
          <Table<DataType>
            columns={columns}
            dataSource={dataSettingList}
            scroll={{ x: 1000 }}
            bordered
            pagination={
              total <= 10
                ? false
                : {
                    current: pagination.pageIndex + 1,
                    pageSize: pagination.pageSize,
                    pageSizeOptions: DEFAULT_PAGE_OPTIONS,
                    total: total,
                    showSizeChanger: true,
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                    onShowSizeChange(current, pageSize) {
                      setPagination({
                        pageIndex: current - 1,
                        pageSize,
                      })
                    },
                    onChange(page, pageSize) {
                      setPagination({
                        pageIndex: page - 1,
                        pageSize,
                      })
                    },
                  }
            }
          />

          <Modal
            title={modalMode === 'update' ? 'Update Setting' : 'Create Setting'}
            open={isModalOpen}
            onOk={handleOk}
            onCancel={handleCancel}
            className="modalSetting"
            destroyOnClose={true}
            width={700}
            centered
          >
            <Form form={form}>
              <Form.Item
                label="Name"
                name="name"
                rules={[{ required: true, message: 'Please input Name!' }]}
              >
                <Input
                  placeholder="Enter Name"
                  style={{ width: '100%' }}
                  disabled={modalMode === 'update' ? true : false}
                />
              </Form.Item>

              <Form.Item
                label="Value"
                name="value"
                rules={[{ required: true, message: 'Please input Value!' }]}
              >
                <Input placeholder="Enter Value" style={{ width: '100%' }} />
              </Form.Item>

              <Form.Item
                label="Status"
                name="isActive"
                rules={[{ required: true, message: 'Please choose isActive!' }]}
              >
                <Select
                  showSearch
                  placeholder="Choose Status"
                  optionFilterProp="label"
                  filterSort={(optionA, optionB) =>
                    String(optionA?.label ?? '')
                      .toLowerCase()
                      .localeCompare(String(optionB?.label ?? '').toLowerCase())
                  }
                  options={statusOptions}
                />
              </Form.Item>
            </Form>
          </Modal>
        </Card>
      </div>
    </>
  )
}
